import React, { forwardRef } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import MediaAPI from "../../api/MediaAPI";
import { FormProviderWithTranslations } from "../../contexts/FormTranslationsContext";
import {
  apiToFormTranslations,
  castingTranslationsFormSchema,
  createTranslationResponseToFormField,
  formToApiTranslations,
} from "../../utils/translationAdapters/casting";
import Input from "../form/Input";
import MarkdownEditorField from "../form/MarkdownEditorField";
import DeletePicturesButton from "../formTranslations/DeletePicturesButton";
import FormPictures from "../formTranslations/FormPictures";
import FormTranslations from "../formTranslations/FormTranslations";
import FormTranslationsHeader from "../formTranslations/FormTranslationsHeader";

import type { Resolver } from "react-hook-form";

import type {
  CreateCastingTranslationResponse,
  GetCastingResponse,
} from "../../api/MediaAPI.types";
import type { LanguageCode } from "../../api/ProjectAPI.types";
import type { PictureKeys } from "../../constants/ratio";
import type {
  FormTranslationsHandle,
  PageFieldArray,
} from "../../contexts/FormTranslationsContext";
import type { CastingTranslationFormField } from "../../utils/translationAdapters/casting";
import type { UploadPictureState } from "../formTranslations/FormPictures";

export type CastingTranslationsHandle = FormTranslationsHandle<
  ReturnType<typeof formToApiTranslations>
>;

interface CastingTranslationsProps {
  casting: GetCastingResponse;
}

const CastingTranslations = forwardRef<
  CastingTranslationsHandle,
  CastingTranslationsProps
>(({ casting }, ref) => {
  const { t } = useTranslation(["editor-casting", "form", "error"]);

  const queryClient = useQueryClient();

  const getCastingQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getCasting,
    params: [casting.uuid],
  });

  const methods = useForm<
    PageFieldArray<CastingTranslationFormField>,
    unknown,
    PageFieldArray<CastingTranslationFormField>
  >({
    mode: "onChange",
    resolver: zodResolver(castingTranslationsFormSchema) as Resolver<
      PageFieldArray<CastingTranslationFormField>
    >,
    defaultValues: {
      translations: apiToFormTranslations(casting.translations),
    },
  });

  const uploadPicture = useMutation({
    mutationFn: async ({
      pictureFile,
      translationId,
      pictureFormat,
    }: {
      pictureFile: File;
      translationId: string;
      pictureFormat: PictureKeys;
    }) =>
      MediaAPI.uploadPicture({
        itemId: casting.uuid,
        pictureFile,
        translationId,
        pictureFormat,
        contentType: "castings",
      }),
    onSuccess(data, variables) {
      const updatedTranslations = casting.translations.map((translation) =>
        translation.uuid === variables.translationId
          ? {
              ...translation,
              picture: data.picture,
              pictures: data.pictures,
              temp_pictures: null,
            }
          : translation,
      );
      queryClient.setQueryData(getCastingQuery.queryKey, {
        ...casting,
        translations: updatedTranslations,
      });

      const formTranslations = methods.getValues("translations");
      const formIndex = formTranslations.findIndex(
        (translation) => translation.uuid === variables.translationId,
      );
      if (formIndex >= 0) {
        methods.setValue(`translations.${formIndex}.picture`, data.picture);
        methods.setValue(`translations.${formIndex}.pictures`, data.pictures);
      }
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handlePictureUpload = ({
    file,
    translationId,
    pictureFormat,
  }: {
    file: File;
    translationId: string;
    pictureFormat: PictureKeys;
  }): void => {
    uploadPicture.mutate({ pictureFile: file, translationId, pictureFormat });
  };

  const uploadPictureState: UploadPictureState = {
    isLoading: uploadPicture.isLoading,
    pictureFormat: uploadPicture.variables?.pictureFormat,
    translationId: uploadPicture.variables?.translationId,
  };

  const deletePicture = useMutation({
    mutationFn: async ({ translationId }: { translationId: string }) =>
      MediaAPI.deletePicture({
        itemId: casting.uuid,
        translationId,
        contentType: "castings",
      }),
    onSuccess(_data, variables) {
      const updatedTranslations = casting.translations.map((translation) =>
        translation.uuid === variables.translationId
          ? { ...translation, picture: null, pictures: null }
          : translation,
      );

      queryClient.setQueryData(getCastingQuery.queryKey, {
        ...casting,
        translations: updatedTranslations,
      });

      const formTranslations = methods.getValues("translations");
      const formIndex = formTranslations.findIndex(
        (translation) => translation.uuid === variables.translationId,
      );

      if (formIndex >= 0) {
        methods.setValue(`translations.${formIndex}.picture`, null);
        methods.setValue(`translations.${formIndex}.pictures`, null);
      }

      toast.success(t("form:deletePicturesSuccess"));
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleAddLanguage = async ({
    languageCode,
    languageUuid,
  }: {
    languageCode: LanguageCode;
    languageUuid: string;
  }): Promise<CreateCastingTranslationResponse | null> => {
    if (!languageUuid) return null;
    try {
      const response = await MediaAPI.createCastingTranslation({
        castingId: casting.uuid,
        languageUuid,
      });

      queryClient.setQueryData(getCastingQuery.queryKey, {
        ...casting,
        translations: [
          ...casting.translations,
          {
            uuid: response.uuid,
            default: response.default,
            biography: response.biography ?? "",
            signature: response.signature ?? "",
            language_uuid: response.language_uuid,
            language: languageCode,
            name: response.name ?? "",
            picture: null,
            pictures: null,
            temp_pictures: null,
          },
        ],
      });

      return response;
    } catch (error) {
      console.error("Error creating translation:", error);
      toast.error(t("genericError", { ns: "error" }));
      return null;
    }
  };

  const updateCastingMutation = useMutation({
    mutationFn: async ({
      uuid,
      data,
    }: {
      uuid: string;
      data: GetCastingResponse;
    }) => MediaAPI.updateCasting({ uuid, data }),
    onSuccess: (updatedCasting) => {
      toast.success(t("form:deleteLanguageSuccess"));
      queryClient.setQueryData(getCastingQuery.queryKey, updatedCasting);
    },
    onError: () => {
      toast.error(t("editor-notif:saveError"));
    },
  });

  const handleDeleteLanguage = async (
    updatedFormTranslations: CastingTranslationFormField[],
  ): Promise<void> => {
    const apiTranslations = formToApiTranslations(updatedFormTranslations);

    await updateCastingMutation.mutateAsync({
      uuid: casting.uuid,
      data: {
        ...casting,
        translations: apiTranslations.translations,
        translations_indexes: apiTranslations.translations_indexes,
      } as GetCastingResponse,
    });
  };

  return (
    <div className="flex flex-col">
      <FormProviderWithTranslations
        formToApiTranslations={formToApiTranslations}
        handleRef={ref}
        methods={methods}
        onAddLanguage={handleAddLanguage}
        onDeleteLanguage={handleDeleteLanguage}
        toFormField={createTranslationResponseToFormField}
      >
        <FormTranslationsHeader />
        <FormTranslations>
          <Input
            isFullWidth
            isRequired
            label={t("title", { ns: "editor-casting" })}
            name="name"
            type="text"
          />
          <MarkdownEditorField
            isFullWidth
            label={t("biography", { ns: "editor-casting" })}
            name="biography"
          />
          <Input
            isFullWidth
            label={t("signature", { ns: "editor-casting" })}
            name="signature"
            type="text"
          />
        </FormTranslations>
        <div className="editor-form-section m-b-0 m-t-20 p-b-0">
          <div className="editor-form-section-title flex items-center gap-[0.625rem]">
            <span>{t("pictures", { ns: "editor-casting" })}</span>
            <DeletePicturesButton onDelete={deletePicture.mutateAsync} />
          </div>
          <FormPictures
            item={casting}
            itemType="castings"
            onUpload={handlePictureUpload}
            uploadPictureState={uploadPictureState}
          />
        </div>
      </FormProviderWithTranslations>
    </div>
  );
});

CastingTranslations.displayName = "CastingTranslations";

export default CastingTranslations;
